/**
 * Transforms API MessageResponse to ChatMessageEntity for Redux storage.
 * Uses the shared message-type-mapping for consistent type resolution
 * across both API reload and WebSocket streaming flows.
 */

import type {
  CoveAnnotation,
  LlmErrorAnnotation,
  MessageResponse,
  MessageStatus,
  ToolResult,
} from '@saaga/shared/interfaces/message.interface'
import {
  isExcludedMessageType,
  resolveEntityType,
} from '@saaga/shared/interfaces/message-type-mapping.helper'
import type {
  AgentReportEntity,
  ChatMessageEntity,
  ChatMessageEntityType,
  ChatParticipant,
  ChatSender,
  DelegationEntity,
  MessageEntity,
  ToolCallEntity,
  ToolStatus,
} from '@saaga/shared/store/slices/chat-message-entity.interface'

import type { UserLookupMap } from '@/hooks/use-org-members-lookup.hook'
import { explodeToolResultsToEntities } from '@/utils/tool-result-entity-builder.util'

const STATUS_MAP: Record<MessageStatus, ToolStatus> = {
  pending: 'pending',
  processing: 'running',
  completed: 'completed',
  failed: 'error',
}

const ROLE_MAP: Record<string, ChatSender> = {
  user: 'user',
  assistant: 'assistant',
}

const mapMessageStatusToToolStatus = (status: MessageStatus): ToolStatus => STATUS_MAP[status]

const mapRoleToSender = (role: string): ChatSender => ROLE_MAP[role] ?? 'system'

const resolveAssistantDisplayName = (message: MessageResponse): string => {
  const metadataLabel = message.msg_metadata?.agent_label as string | undefined
  return metadataLabel ?? message.agent_name ?? 'Agent'
}

const createParticipant = (
  message: MessageResponse,
  userLookup?: UserLookupMap
): ChatParticipant => {
  const isAgentItem = message.role === 'assistant' || message.role === 'tool'

  if (isAgentItem) {
    return {
      id: message.agent_name ?? 'assistant',
      name: resolveAssistantDisplayName(message),
      avatarUrl: undefined,
    }
  }

  const lookupEntry = userLookup?.get(message.user_id)

  if (lookupEntry) {
    return { id: lookupEntry.id, name: lookupEntry.name, avatarUrl: lookupEntry.avatarUrl }
  }

  return { id: message.user_id, name: message.role, avatarUrl: undefined }
}

const extractHtmlResultKey = (toolResults: MessageResponse['tool_results']): string | undefined => {
  if (!toolResults || toolResults.length === 0) return undefined
  const firstResult = toolResults[0]
  return firstResult?.html_result_key ?? undefined
}

/**
 * Match the `no_data` key in a Python repr or JSON string.
 * Backend stores tool results via Python's `str()`, producing e.g.
 *   "{'no_data': True, 'message': '...'}"
 * This regex matches both Python (`'no_data': True`) and JSON (`"no_data": true`)
 * formats without attempting a full parse of the repr string.
 */
const NO_DATA_PATTERN = /['"]no_data['"]\s*:\s*[Tt]rue/

const extractNoDataFromToolResults = (toolResults: ToolResult[] | null): boolean => {
  if (!toolResults?.length) return false
  const result = toolResults[0].result
  if (!result) return false
  if (result.data && (result.data as Record<string, unknown>).no_data === true) return true
  return typeof result.preview === 'string' && NO_DATA_PATTERN.test(result.preview)
}

const resolveToolStatusFromMessage = (message: MessageResponse): ToolStatus => {
  const isNoData =
    message.status === 'completed' && extractNoDataFromToolResults(message.tool_results)
  return isNoData ? 'no-data' : mapMessageStatusToToolStatus(message.status)
}

interface MetadataFields {
  threadId?: string
  parentThreadId: string | null
  turnStartedAt: string
  agentLabel?: string
  schemaVersion?: number
  cove?: CoveAnnotation
  llmError?: LlmErrorAnnotation
}

const buildMetadataFields = (message: MessageResponse): MetadataFields => {
  const metadata = message.msg_metadata
  const turnStartedAt = (metadata?.turn_started_at as string | undefined) ?? message.created_at
  return {
    threadId: metadata?.agent_execution_thread_id as string | undefined,
    parentThreadId: (metadata?.parent_thread_id as string | null | undefined) ?? null,
    turnStartedAt,
    agentLabel: metadata?.agent_label as string | undefined,
    schemaVersion: message.schema_version ?? undefined,
    cove: metadata?.cove,
    llmError: metadata?.error,
  }
}

interface BaseTransformParams {
  message: MessageResponse
  participant: ChatParticipant
  metadata: MetadataFields
}

interface CommonEntityFields {
  id: string
  conversationId: string
  timestamp: string
  threadId?: string
  parentThreadId: string | null
  turnStartedAt: string
  agentLabel?: string
  schemaVersion?: number
  stepCount?: number
  isPending: false
  isStreaming: boolean
  streamingContent: ''
}

const buildCommonFields = (
  message: MessageResponse,
  metadata: MetadataFields
): CommonEntityFields => ({
  id: message.id,
  conversationId: message.conversation_id,
  timestamp: message.created_at,
  threadId: metadata.threadId,
  parentThreadId: metadata.parentThreadId,
  turnStartedAt: metadata.turnStartedAt,
  agentLabel: metadata.agentLabel,
  schemaVersion: metadata.schemaVersion,
  stepCount: message.step_count ?? undefined,
  isPending: false,
  isStreaming: message.status === 'processing',
  streamingContent: '',
})

const buildMessageEntity = ({
  message,
  participant,
  metadata,
}: BaseTransformParams): MessageEntity => ({
  ...buildCommonFields(message, metadata),
  type: 'message',
  content: message.thinking ?? message.content,
  sender: mapRoleToSender(message.role),
  participant,
  attachmentList: message.attachments?.length ? message.attachments : undefined,
  status: message.status,
  cove: metadata.cove,
  llmError: metadata.llmError,
})

const buildLegacyToolCallEntity = ({
  message,
  participant,
  metadata,
}: BaseTransformParams): ToolCallEntity => ({
  ...buildCommonFields(message, metadata),
  type: 'tool-call',
  content: message.thinking ?? message.content,
  sender: mapRoleToSender(message.role),
  participant,
  toolName: message.tool_name ?? 'unknown',
  friendlyName: message.tool_name ?? undefined,
  toolStatus: resolveToolStatusFromMessage(message),
  toolInput: message.tool_args ? JSON.stringify(message.tool_args, null, 2) : undefined,
  toolOutput: message.tool_results ? JSON.stringify(message.tool_results, null, 2) : undefined,
  toolErrorMessage: message.error_message ?? undefined,
  htmlResultKey: extractHtmlResultKey(message.tool_results),
})

const buildAgentReportEntity = ({
  message,
  participant,
  metadata,
}: BaseTransformParams): AgentReportEntity => ({
  ...buildCommonFields(message, metadata),
  type: 'agent-report',
  content: message.content,
  participant,
  agentId: message.agent_name ?? 'unknown',
  agentName: participant.name,
  reportContent: message.content,
  reportId:
    typeof message.msg_metadata?.report_id === 'string'
      ? message.msg_metadata.report_id
      : undefined,
  reportVersion:
    typeof message.msg_metadata?.report_version === 'number'
      ? message.msg_metadata.report_version
      : undefined,
  isMasterArtifact: message.msg_metadata?.is_master_artifact === true,
  reportFormat: message.msg_metadata?.report_format === 'markdown' ? 'markdown' : 'html',
})

const buildDelegationEntity = ({ message, metadata }: BaseTransformParams): DelegationEntity => {
  const meta = message.msg_metadata
  const targetSlug = meta?.delegation_target_slug as string | undefined
  const targetLabel = meta?.delegation_target_label as string | undefined
  const supervisorLabel = meta?.supervisor_label as string | undefined

  return {
    ...buildCommonFields(message, metadata),
    type: 'delegation',
    content: message.content,
    supervisorId: supervisorLabel ?? message.agent_name ?? 'unknown',
    delegationTargetList: targetSlug ? [targetSlug] : [],
    delegationTargetLabelList: targetLabel ? [targetLabel] : [],
    isDelegationComplete: message.status === 'completed',
    delegationReport: message.content || undefined,
  }
}

const ENTITY_BUILDERS: Record<
  ChatMessageEntityType,
  (params: BaseTransformParams) => ChatMessageEntity
> = {
  message: buildMessageEntity,
  note: buildMessageEntity,
  'content-brief': buildMessageEntity,
  'tool-call': buildLegacyToolCallEntity,
  'agent-report': buildAgentReportEntity,
  delegation: buildDelegationEntity,
}

const fixupEntityType = (entity: ChatMessageEntity, type: ChatMessageEntityType): void => {
  if (entity.type === type) return
  ;(entity as { type: ChatMessageEntityType }).type = type
}

export const transformMessageToEntity = (
  message: MessageResponse,
  userLookup?: UserLookupMap
): ChatMessageEntity | null => {
  if (isExcludedMessageType(message.message_type)) return null
  const entityType = resolveEntityType(message.message_type)
  const builder = ENTITY_BUILDERS[entityType]
  const participant = createParticipant(message, userLookup)
  const metadata = buildMetadataFields(message)
  const entity = builder({ message, participant, metadata })
  fixupEntityType(entity, entityType)
  return entity
}

const isLegacyToolCallRow = (message: MessageResponse): boolean =>
  message.message_type === 'TOOL_CALL'

const hasNewToolResults = (message: MessageResponse): boolean =>
  !isLegacyToolCallRow(message) && (message.tool_results?.length ?? 0) > 0

const transformMessageWithExplodedTools = (
  message: MessageResponse,
  userLookup?: UserLookupMap
): ChatMessageEntity[] => {
  const entities: ChatMessageEntity[] = []

  if (message.content && message.content.trim().length > 0) {
    const baseEntity = transformMessageToEntity(message, userLookup)
    if (baseEntity) entities.push(baseEntity)
  }

  const participant = createParticipant(message, userLookup)
  entities.push(...explodeToolResultsToEntities(message, participant))

  return entities
}

const transformSingleMessage = (
  message: MessageResponse,
  userLookup?: UserLookupMap
): ChatMessageEntity[] => {
  const entity = transformMessageToEntity(message, userLookup)
  return entity ? [entity] : []
}

export const transformMessageListToEntityList = (
  messageList: MessageResponse[],
  userLookup?: UserLookupMap
): ChatMessageEntity[] =>
  messageList.reduce<ChatMessageEntity[]>((resultList, msg) => {
    if (isExcludedMessageType(msg.message_type)) return resultList
    const entities = hasNewToolResults(msg)
      ? transformMessageWithExplodedTools(msg, userLookup)
      : transformSingleMessage(msg, userLookup)
    resultList.push(...entities)
    return resultList
  }, [])
